<template>
  <ETooltip v-if="displayComponent" :side="tooltipSide" class="domain-label" data-test-id="domain-label" @tooltip-opened="tooltipOpened">
    <span class="domain-label__toggle">
      <span class="domain-label__title" data-test-id="domain-label-title">{{ accessibleTitle }}</span>
      <EIcon
        :icon="labelIcon"
        size="m"
        class="icon"
        :data-test-id="`${labelIcon}-icon`"
      />
    </span>
    <template #content>
      <div data-test-id="tooltip-text">
        <template v-if="isGreenLeaf">
          {{ $t('common.domainlabel.greenleaf') }}
        </template>
        <template v-else-if="isClimatePledgeRating">
          <span v-safe-html="climatePledgeTooltipText" />
        </template>
        <template v-else-if="isFossilFuel">
          {{ $t('common.domainlabel.fossilfuel') }}
        </template>
      </div>
      <div class="tooltip__footer">
        <ELink
          v-if="!isClimatePledgeRating"
          class="tooltip__link"
          :href="learnMoreUrl"
          rel="noopener"
          target="_blank"
          data-test-id="learn-more-link"
          @click="onLinkClick"
        >
          {{ $t('common.domainlabel.learnmore') }}
        </ELink>
        <ELink
          v-if="!isClimatePledgeRating"
          class="tooltip__link"
          color="secondary"
          v-bind="$attrs"
          role="button"
          tabindex="0"
          data-test-id="feedback-link"
          :aria-pressed="!!modalActive"
          @click="onFeedbackClick"
          @keydown.enter.space.stop.prevent="onFeedbackClick"
        >
          {{ $t('common.footer.feedback') }}
        </ELink>
        <ELink
          v-if="isClimatePledgeRating"
          v-analytics.click="trackSeeWhatItMeansClick"
          class="tooltip__link tooltip__link--blue"
          color="secondary"
          v-bind="$attrs"
          role="button"
          tabindex="0"
          data-test-id="what-it-means-link"
          :aria-pressed="!!modalActive"
          @click="onWhatItMeansClick"
          @keydown.enter.space.stop.prevent="onWhatItMeansClick"
        >
          {{ $t('common.green.climatepledge.seewhatitmeans') }}
        </ELink>
      </div>
    </template>
    <EFeedbackModal v-if="!isClimatePledgeRating" v-bind="feedbackModal" :active="modalActive" @close="onClose" />
    <EClimatePledgeModal
      v-if="isClimatePledgeRating"
      :active="modalActive && !cpFeedbackActive"
      :company-info="climatePledgeCompanyInfo"
      :rating="domainHighlighting.ecosiaLabel"
      trigger-location="tooltip"
      @close="onClose"
      @cp-feedback-link-click="onCPFeedbackLinkClick"
    />
    <EFeedbackModal
      v-if="isClimatePledgeRating"
      v-bind="climatePledgeFeedbackModal"
      :active="!modalActive && cpFeedbackActive"
      @close="onCPFeedbackClose"
    />
  </ETooltip>
</template>
<script>
import { mapState, mapGetters } from 'vuex';
import { GREEN_SEARCH, GREEN_SEARCH_FR, GREEN_SEARCH_DE } from '~~/common/js/constants/urls';
import screen from '~~/common/vue/mixins/screen';
import EIcon from '~~/common/vue/components/icon';
import ETooltip from '~~/common/vue/components/tooltip';
import ELink from '~~/common/vue/components/link';
import EFeedbackModal from '~~/common/vue/components/feedback-modal';
import EClimatePledgeModal from '~~/search/common/vue/components/climate-pledge/climate-pledge-modal';
import { sendEvent } from '~~/common/js/client/analytics';
import { GREEN_FEATURES } from '~~/search/common/js/client/feature-flags';
import { trackImpression, PAGE_SERP, TYPE_RICH } from '~~/search/common/js/client/analytics/rich-content';
import companyMetaData from '~~/search/common/vue/components/climate-pledge/companyMetaData';
import {
  climatePledgeTooltipDisplay,
  climatePledgeTooltipSeeMeaningClick,
  climatePledgeRatingImpression,
} from '~~/search/common/js/client/analytics/climate-pledge-ratings';

export const GREEN_LEAF = 'GREEN_LEAF';
export const GREEN_LEAF_TRACKING = 'green_domain';
export const FOSSIL_FUEL = 'FOSSIL_FUEL';
export const FOSSIL_FUEL_TRACKING = 'coal_domain';
export const NONE = 'NONE';
export const CLIMATE_PLEDGE_RATINGS = [
  'CLIMATE_PLEDGE_RATING_A',
  'CLIMATE_PLEDGE_RATING_B',
  'CLIMATE_PLEDGE_RATING_C',
  'CLIMATE_PLEDGE_RATING_D',
  'CLIMATE_PLEDGE_RATING_F',
];
const ICONS = {
  GREEN_LEAF: 'green-leaf',
  FOSSIL_FUEL: 'fossil-fuel',
  CLIMATE_PLEDGE_RATING_A: 'climate-pledge-rating-a',
  CLIMATE_PLEDGE_RATING_B: 'climate-pledge-rating-b',
  CLIMATE_PLEDGE_RATING_C: 'climate-pledge-rating-c',
  CLIMATE_PLEDGE_RATING_D: 'climate-pledge-rating-d',
  CLIMATE_PLEDGE_RATING_F: 'climate-pledge-rating-f',
};
const TITLE = {
  GREEN_LEAF: 'greenleaf',
  FOSSIL_FUEL: 'fossilfuel',
};

export default {
  name: 'EDomainLabel',
  components: {
    EIcon,
    ETooltip,
    ELink,
    EFeedbackModal,
    EClimatePledgeModal,
  },
  mixins: [screen()],
  props: {
    domainHighlighting: {
      type: Object,
      default: null,
    },
    triggerUrl: {
      type: String,
      default: '',
    },
  },
  data() {
    return {
      modalActive: false,
      cpFeedbackActive: false,
    };
  },
  computed: {
    ...mapState('global', ['locale']),
    ...mapGetters('goodall', ['getExperiment']),
    tooltipSide() {
      return this.isDesktopUp ? 'right-center' : 'left-center';
    },
    domainLabel() {
      return this.domainHighlighting && this.domainHighlighting.ecosiaLabel ?
        this.domainHighlighting.ecosiaLabel : NONE;
    },
    displayComponent() {
      if (this.isClimatePledgeRating) {
        return !!this.hasGreenFeaturesEnabled;
      }
      return this.domainLabel !== NONE && !CLIMATE_PLEDGE_RATINGS.includes(this.domainLabel);
    },
    labelIcon() {
      if (this.domainLabel !== NONE) {
        return ICONS[this.domainLabel];
      }
      return '';
    },
    climatePledgeTitle() {
      return this.domainHighlighting?.ecosiaLabel.split('_').join(' ').toLowerCase();
    },
    accessibleTitle() {
      let label = '';
      if (this.isClimatePledgeRating) {
        return this.climatePledgeTitle;
      }
      if (this.isGreenLeaf || this.isFossilFuel) {
        label = TITLE[this.domainLabel];
        return this.$t(`common.domainlabel.title.${label}`);
      }
      return label;
    },
    isGreenLeaf() {
      return this.domainLabel === GREEN_LEAF;
    },
    isFossilFuel() {
      return this.domainLabel === FOSSIL_FUEL;
    },
    learnMoreUrl() {
      switch (this.locale) {
        case 'fr':
          return GREEN_SEARCH_FR;
        case 'de':
          return GREEN_SEARCH_DE;
        default:
          return GREEN_SEARCH;
      }
    },
    feedbackModal() {
      let subject = '';

      if (this.isFossilFuel) {
        subject = 'fossil-fuel-domain';
      }

      if (this.isGreenLeaf) {
        subject = 'green-domain';
      }

      return {
        errorMessage: this.$t('common.header.feedback.error'),
        options: [
          { label: this.$t('common.header.feedback.bullet1'), value: 'helpful' },
          { label: this.$t('common.header.feedback.bullet2'), value: 'missing' },
          { label: this.$t('common.header.feedback.bullet3'), value: 'wrong' },
          { label: this.$t('common.header.feedback.bullet4'), value: 'not_helpful' },
        ],
        query: this.$route.query.q?.toString(),
        submitText: this.$t('common.header.feedback.send'),
        successMessage: this.$t('common.header.feedback.body2'),
        successTitle: this.$t('common.header.feedback.title2'),
        textareaPlaceholder: this.$t('common.header.feedback.tellmore'),
        title: this.$t('common.header.feedback.login'),
        ticketSubject: `Feedback - Search - ${subject}`,
        url: process.env.feedbackUrl,
        widgetType: 'domain_label_rich',
      };
    },
    isClimatePledgeRating() {
      const hasRating = this.climatePledgeRatingsText[this.domainLabel];
      return !!hasRating;
    },
    climatePledgeTooltipText() {
      return this.climatePledgeRatingsText[this.domainLabel];
    },
    climatePledgeRatingsText() {
      return {
        CLIMATE_PLEDGE_RATING_A: this.$t('common.green.climatepledge.tooltiptext.leading'),
        CLIMATE_PLEDGE_RATING_B: this.$t('common.green.climatepledge.tooltiptext.good'),
        CLIMATE_PLEDGE_RATING_C: this.$t('common.green.climatepledge.tooltiptext.modest'),
        CLIMATE_PLEDGE_RATING_D: this.$t('common.green.climatepledge.tooltiptext.weak'),
        CLIMATE_PLEDGE_RATING_F: this.$t('common.green.climatepledge.tooltiptext.failing'),
      };
    },
    hasGreenFeaturesEnabled() {
      return this.getExperiment(GREEN_FEATURES.flag)?.includes(GREEN_FEATURES.enabled);
    },
    climatePledgeCompanyInfo() {
      const { organization } = this.domainHighlighting;
      return organization.parentName ?
        {
          ...companyMetaData[organization.parentName],
          name: organization.parentName,
        } :
        {
          ...companyMetaData[organization.name],
          name: organization.name,
        };
    },
    tooltipOpenedEvent() {
      return new Set();
    },
    trackSeeWhatItMeansClick() {
      return climatePledgeTooltipSeeMeaningClick;
    },
    climatePledgeFeedbackModal() {
      return {
        errorMessage: this.$t('common.header.feedback.error'),
        options: [
          { label: this.$t('common.header.feedback.bullet1'), value: 'helpful' },
          { label: this.$t('common.header.feedback.bullet2'), value: 'missing' },
          { label: this.$t('common.header.feedback.bullet3'), value: 'wrong' },
          { label: this.$t('common.header.feedback.bullet4'), value: 'not_helpful' },
        ],
        submitText: this.$t('common.header.feedback.send'),
        successMessage: this.$t('common.header.feedback.body2'),
        successTitle: this.$t('common.header.feedback.title2'),
        textareaPlaceholder: this.$t('common.header.feedback.tellmore'),
        title: this.$t('common.header.feedback.login'),
        ticketSubject: 'Feedback - Green - Climate Pledge Rating overlay',
        url: null,
        widgetType: 'climate_pledge_entity',
        query: this.$route.query.q?.toString(),
        bingMarketCode: this.bingMarketCode,
      };
    },
  },
  mounted() {
    if (this.displayComponent) {
      if (this.isClimatePledgeRating) {
        this.$track(climatePledgeRatingImpression({
          url: this.triggerUrl,
          query: this.$route.query.q?.toString(),
        }));
      } else {
        let name = '';
        if (this.isGreenLeaf) {
          name = GREEN_LEAF_TRACKING;
        }
        if (this.isFossilFuel) {
          name = FOSSIL_FUEL_TRACKING;
        }
        trackImpression({
          name,
          page: PAGE_SERP,
          type: TYPE_RICH,
          triggerUrl: this.triggerUrl,
        });
      }
    }
  },
  methods: {
    onFeedbackClick() {
      this.modalActive = true;
    },
    onWhatItMeansClick() {
      this.modalActive = true;
    },
    onClose() {
      this.modalActive = false;
    },
    onCPFeedbackClose() {
      this.cpFeedbackActive = false;
    },
    onLinkClick() {
      if (this.isGreenLeaf) {
        sendEvent('domain_label', GREEN_LEAF_TRACKING);
      } else if (this.isFossilFuel) {
        sendEvent('domain_label', FOSSIL_FUEL_TRACKING);
      }
    },
    tooltipOpened() {
      if (this.isClimatePledgeRating) {
        const key = [this.triggerUrl, this.domainLabel].join();
        const has = this.tooltipOpenedEvent.has(key);
        if (!has) {
          this.tooltipOpenedEvent.add(key);
          this.$track(climatePledgeTooltipDisplay);
        }
      }
    },
    onCPFeedbackLinkClick() {
      this.modalActive = false;
      this.cpFeedbackActive = true;
    },
  },
};
</script>
<style lang="scss" scoped>
.domain-label {
  margin-left: $space-1s;
}

.domain-label__title {
  @extend %sr-only;
}

.tooltip__footer {
  margin-top: $space-1s;
}

.tooltip__link:not(:first-child) {
  margin-left: $space-1s;
}

.tooltip__link--blue {
  color: var(--color-link-results-default);
}

.domain-label__toggle {
  // This is a temporary solution until we redesign the domain label.
  // If we used the regular space-4l tappable area the label would potentially
  // obscure the result title underneath - a user would want to tap on the result
  // but would instead open the domain label tooltip.
  // TODO: remove temporary solution when the label design is updated.
  $temporary-smaller-tappable-size: $space-3l;

  &::after {
    content: '';
    position: absolute;
    left: 0;
    width: $temporary-smaller-tappable-size;
    height: $temporary-smaller-tappable-size;
    bottom: 0;

    @include desktop {
      content: none;
    }
  }
}
</style>
